import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfirmDialog from "../components/UI/ConfirmDialog";

/**
 * Promise-based replacement for `window.confirm`.
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   if (!(await confirm({ title: "Delete note?" }))) return;
 *   ...
 *   return (<>{confirmDialog}</>);
 *
 * Resolves `true` when confirmed, `false` on cancel, Escape, backdrop click,
 * or unmount — so an awaiting caller can never hang.
 */
export default function useConfirm() {
  const [options, setOptions] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOptions(null);
    resolve?.(result);
  }, []);

  useEffect(() => () => resolverRef.current?.(false), []);

  const confirm = useCallback((opts) => {
    // Only one prompt at a time — a new request supersedes any pending one.
    resolverRef.current?.(false);
    setOptions(opts || {});
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const confirmDialog = (
    <ConfirmDialog
      open={Boolean(options)}
      title={options?.title || "Are you sure?"}
      message={options?.message}
      confirmText={options?.confirmText}
      cancelText={options?.cancelText}
      tone={options?.tone}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, confirmDialog };
}
